/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect } from "react";
import { captureException } from "../../../../constant/sentryConfiguration";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { useNavigationUrl } from "../../../../hooks/useNavigationUrl";
import { saveItineraryDetailsAPI } from "../../../../api/itinerary/saveItineraryDetails";
import { resolveItineraryIdAPI } from "../../../../api/itinerary/resolveItineraryId";
import { getItineraryImageURL } from "../../../../api/llm/getItineraryImageURL";
import { calcNoOfDaysSelected } from "../../../../utils/dateUtils";
import { ROUTES } from "../../../../constant";
import { DialogSelected } from "../../../../constant/common";
import useOpenBookedTrip from "../../../reusableComponent/checkout/useOpenBookedTrip";


function useCreateItineraryHook(
    setIsLoading: any,
    session_Id: any,
    selectedChatDataIdRef: any,
    confirmBookingAPI: any,
    isBookingFailed: any,
    isMobile: any,
    noPayment: boolean = false,
) {
    const state = useContext(StateContext);
    const {
        selectedChatData,
        bookedtransIds,
        userInfo,
        accessJWT,
        createItineraryPayload,
        setLoginCreateAccountState,
    }: iGlobalContext = state;
    const openBookedTrip = useOpenBookedTrip();
    const { navigateWithParams } = useNavigationUrl();
    const { itineraryID = "", flightTransactionId = "", hotelTransactionId = "", activityTransactionId = "" } = bookedtransIds || {};

    const createItineraryAPI = useCallback(
        async ({
            bookedHotelTransactionId,
            bookedFlightTransactionId,
            bookedActivityTransactionId,
        }: {
            bookedHotelTransactionId: string;
            bookedFlightTransactionId: string;
            bookedItineraryId: string;
            bookedActivityTransactionId?: string;
        }) => {
            setIsLoading(true);

            const tripName = createItineraryPayload?.tripName || "";
            let imageURL = "";
            try {
                const imageResponse: any = await getItineraryImageURL(accessJWT, tripName);
                imageURL = imageResponse?.data?.image || "";
            } catch {
                imageURL = "";
            }

            const itineraryPayload: any = {
                tripName,
                userId: userInfo?.id,
                status: "saved",
                hotelTransactionId: bookedHotelTransactionId,
                flightTransactionId: bookedFlightTransactionId,
                startDate: createItineraryPayload?.travelFromDate,
                endDate: createItineraryPayload?.travelToDate,
                numberOfDaysSelected: calcNoOfDaysSelected(
                    createItineraryPayload?.travelFromDate,
                    createItineraryPayload?.travelToDate
                ),
                metaData: {
                    selectedChatData: {
                        destinationList: [{ imageURL }],
                    },
                },
            };
            if (bookedActivityTransactionId) {
                itineraryPayload.activityTransactionId = bookedActivityTransactionId;
            }

            const response = await saveItineraryDetailsAPI(
                accessJWT,
                itineraryPayload,
                "POST",
                itineraryID || undefined
            );

            if (response?.statusCode === 200) {
                if (!response.data.id || !session_Id) return;

                selectedChatDataIdRef.current = response.data?.id;

                confirmBookingAPI({
                    bookedFlightTransactionId: flightTransactionId,
                    bookedHotelTransactionId: hotelTransactionId,
                    bookedItineraryId: itineraryID,
                    startDate: createItineraryPayload?.travelFromDate,
                    endDate: createItineraryPayload?.travelToDate,
                    numberOfDaysSelected: calcNoOfDaysSelected(
                        createItineraryPayload?.travelFromDate,
                        createItineraryPayload?.travelToDate
                    ),
                });
            } else {
                const response = await saveItineraryDetailsAPI(
                    accessJWT,
                    itineraryPayload,
                    "POST",
                    itineraryID || undefined
                );
                if (response?.statusCode !== 200) {
                    captureException({
                        error: "SAVE ITINEARAY ERROR",
                        ...itineraryPayload,
                    });
                } else if (response?.data?.id) {
                    // Retry succeeded — capture the PK so confirmBookingAPI sends
                    // a real id (deep link stays correct on the create path too).
                    selectedChatDataIdRef.current = response.data.id;
                }
                confirmBookingAPI({
                    bookedFlightTransactionId: flightTransactionId,
                    bookedHotelTransactionId: hotelTransactionId,
                    bookedItineraryId: itineraryID,
                    startDate: createItineraryPayload?.travelFromDate,
                    endDate: createItineraryPayload?.travelToDate,
                    numberOfDaysSelected: calcNoOfDaysSelected(
                        createItineraryPayload?.travelFromDate,
                        createItineraryPayload?.travelToDate
                    ),
                });
            }
        },
        [
            accessJWT,
            confirmBookingAPI,
            createItineraryPayload?.travelFromDate,
            createItineraryPayload?.travelToDate,
            createItineraryPayload?.tripName,
            flightTransactionId,
            hotelTransactionId,
            activityTransactionId,
            itineraryID,
            session_Id,
            userInfo?.id,
        ]
    );

    useEffect(() => {
        if (!session_Id && !noPayment) {
            navigateWithParams(ROUTES.Home, { replace: true });
            return;
        }

        // Clear activity booking flow state after successful payment
        try {
            sessionStorage.removeItem("activity_booking_flow_state");
            sessionStorage.removeItem("activity_review_state");
        } catch {}


        // Direct (Shop Travel) bookings: prebook in TravelerCrumbsDetailsContext
        // already POSTed `/app/v1/users/itineraries` and set
        // selectedChatData.id to the new row's id. If that state update hadn't
        // round-tripped through localStorage by the time this effect ran, the
        // old `!selectedChatData?.id` check would fire createItineraryAPI a
        // SECOND time, creating a duplicate itinerary row. `itineraryID` from
        // bookedtransIds is always populated once prebook succeeds (which is
        // a precondition for reaching the success page), so use it as a more
        // reliable signal that the itinerary record already exists — fall back
        // to creating only if neither is set.
        if (!selectedChatData?.id && !itineraryID) {

            createItineraryAPI({
                bookedFlightTransactionId: flightTransactionId,
                bookedHotelTransactionId: hotelTransactionId,
                bookedItineraryId: itineraryID,
                bookedActivityTransactionId: activityTransactionId,
            });
        } else {
            // The itinerary row already exists (prebook created it, itineraryID
            // is set) but selectedChatData.id is empty — the prebook save raced
            // the Stripe redirect or failed to persist. Recover the app PK from
            // the BE short-code BEFORE confirming so the confirmation email/push
            // deep link is correct. This is best-effort and NON-BLOCKING: the
            // backend no longer rejects a missing id, so confirmation proceeds
            // even if recovery fails (network / expired JWT / no row yet).
            (async () => {
                if (
                    !selectedChatData?.id &&
                    !selectedChatDataIdRef.current &&
                    itineraryID &&
                    userInfo?.id
                ) {
                    try {
                        const resolved = await resolveItineraryIdAPI(accessJWT, itineraryID, userInfo.id);
                        if (resolved?.id) selectedChatDataIdRef.current = resolved.id;
                    } catch { /* non-blocking — confirm regardless */ }
                }
                confirmBookingAPI({
                    bookedFlightTransactionId: flightTransactionId,
                    bookedHotelTransactionId: hotelTransactionId,
                    bookedItineraryId: itineraryID,
                    startDate: createItineraryPayload?.travelFromDate,
                    endDate: createItineraryPayload?.travelToDate,
                    numberOfDaysSelected: calcNoOfDaysSelected(
                        createItineraryPayload?.travelFromDate,
                        createItineraryPayload?.travelToDate
                    ),
                });
            })();
        }
    }, [session_Id]);

    useEffect(() => {
        if (isBookingFailed || !itineraryID)
            return setLoginCreateAccountState({
                flag: true,
                page: DialogSelected.BOOKING_FAILURE,
            });
    }, [isBookingFailed, itineraryID]);

    const handleViewItinerary = useCallback(
        (responseTripId?: string) => {
            openBookedTrip(selectedChatData?.id || selectedChatDataIdRef.current || itineraryID, isMobile);
        },
        [openBookedTrip, isMobile, selectedChatData?.id, itineraryID]
    );
    return {
        handleViewItinerary,
        createItineraryAPI,
    }
}

export default useCreateItineraryHook